import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle, Plus, Edit, Trash2, Loader2, AlertTriangle, Calendar, Eye, CreditCard, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import DraftLimitModal from '../../components/will/DraftLimitModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerWillManagement = () => {
  const navigate = useNavigate();
  const [wills, setWills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [showDraftLimitModal, setShowDraftLimitModal] = useState(false);
  const [maxUnpaidDrafts, setMaxUnpaidDrafts] = useState(10);
  const partnerStatus = localStorage.getItem('partnerStatus') || 'Active';
  const isPending = partnerStatus === 'Pending';

  const partnerToken = localStorage.getItem('partnerToken');
  const axiosConfig = { headers: { Authorization: `Bearer ${partnerToken}` } };

  useEffect(() => {
    if (!isPending) {
      fetchWills();
      fetchPricing();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/partner/will-pricing`, axiosConfig);
      if (response.data.max_unpaid_drafts) {
        setMaxUnpaidDrafts(response.data.max_unpaid_drafts);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const fetchWills = async () => {
    try {
      const response = await axios.get(`${API}/partner/wills`, axiosConfig);
      setWills(response.data.wills || []);
    } catch (error) {
      console.error('Error fetching wills:', error);
      toast.error('Failed to load wills');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWill = async () => {
    const unpaidDrafts = wills.filter(w => !w.is_paid && w.status !== 'completed');
    if (unpaidDrafts.length >= maxUnpaidDrafts) {
      setShowDraftLimitModal(true);
      return;
    }

    setCreatingDraft(true);
    try {
      const response = await axios.post(`${API}/partner/will/draft`, {}, axiosConfig);
      if (response.data.success && response.data.will_id) {
        toast.success('Draft will created! Start filling in the details.');
        navigate(`/partner/will/create/${response.data.will_id}`);
      } else {
        toast.error('Failed to create draft will');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create draft will';
      toast.error(errorMessage);
    } finally {
      setCreatingDraft(false);
    }
  };

  const handleEditWill = (willId) => {
    navigate(`/partner/will/create/${willId}`);
  };

  const handleDeleteWill = async (willId) => {
    if (!window.confirm('Are you sure you want to delete this Will? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/partner/will/${willId}`, axiosConfig);
      toast.success('Will deleted successfully');
      fetchWills();
    } catch (error) {
      toast.error('Failed to delete will');
    }
  };

  const handleDeleteDraftFromLimit = async (willId) => {
    try {
      await axios.delete(`${API}/partner/will/${willId}`, axiosConfig);
      toast.success('Draft deleted successfully');
      setShowDraftLimitModal(false);
      fetchWills();
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30', textColor: 'text-emerald-400', label: 'Completed' };
      case 'draft':
      default:
        return { icon: Clock, bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', textColor: 'text-amber-400', label: 'Draft' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="partner-wills-loading">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
        <p className="text-slate-400">Loading Wills...</p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6" data-testid="partner-wills-pending">
        <div className="w-20 h-20 rounded-full bg-amber-500/10 flex items-center justify-center">
          <ShieldAlert className="w-10 h-10 text-amber-400" />
        </div>
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-white mb-3">Account Pending Approval</h2>
          <p className="text-slate-400 leading-relaxed">
            Your partner account is currently under review. The Wills section will be available once your account is approved by our admin team.
          </p>
          <p className="text-slate-500 text-sm mt-4">
            This typically takes 1-2 business days. You will receive an email once approved.
          </p>
        </div>
        <Button
          onClick={() => navigate('/partner/dashboard')}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-800 mt-2"
        >
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="partner-will-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Will Management</h1>
          <p className="text-slate-400 mt-1">Create and manage wills on behalf of your clients</p>
        </div>
        <Button
          onClick={handleCreateWill}
          disabled={creatingDraft}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          data-testid="partner-create-will-btn"
        >
          {creatingDraft ? (
            <><Loader2 size={18} className="mr-2 animate-spin" /> Creating Draft...</>
          ) : (
            <><Plus size={18} className="mr-2" /> Create New Will</>
          )}
        </Button>
      </div>

      {/* Wills List or Empty State */}
      {wills.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-6 rounded-full bg-slate-800/50">
                <FileText className="w-16 h-16 text-slate-600" />
              </div>
              <h2 className="text-2xl font-bold text-white">No Wills Created Yet</h2>
              <p className="text-slate-400 max-w-md">
                Start creating wills for your clients. You can fill in all the details on their behalf.
              </p>
              <Button
                onClick={handleCreateWill}
                disabled={creatingDraft}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold mt-4"
                data-testid="partner-create-first-will-btn"
              >
                {creatingDraft ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Creating Draft...</>
                ) : (
                  <><Plus size={18} className="mr-2" /> Create First Will</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {wills.map((will) => {
            const statusConfig = getStatusConfig(will.status);
            const StatusIcon = statusConfig.icon;
            const currentStep = will.progress?.current_step || 1;
            const totalSteps = 9;
            const progressPercent = Math.round((currentStep / totalSteps) * 100);
            const isExpired = will.is_expired;
            const daysRemaining = will.days_remaining;
            const expiryDateFormatted = will.expiry_date_formatted;
            const showExpiryWarning = daysRemaining !== null && daysRemaining <= 30 && !isExpired;
            const isUnpaid = !will.is_paid && will.status !== 'completed';

            return (
              <Card
                key={will.id}
                className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all cursor-pointer ${isExpired ? 'border-red-500/30' : ''} ${isUnpaid ? 'border-amber-500/20' : ''}`}
                onClick={() => handleEditWill(will.id)}
                data-testid={`partner-will-card-${will.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="p-3 rounded-xl bg-slate-800/50">
                      <FileText className={`w-8 h-8 ${isExpired ? 'text-red-400' : isUnpaid ? 'text-amber-400/60' : 'text-amber-400'}`} />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.borderColor} border`}>
                        {statusConfig.label}
                      </span>
                      {isUnpaid && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Unpaid
                        </span>
                      )}
                      {isExpired && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/30">
                          Expired
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {will.testator_name || 'Unnamed Will'}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Created: {new Date(will.created_at).toLocaleDateString()}
                    </p>
                    {will.updated_at && will.updated_at !== will.created_at && (
                      <p className="text-sm text-slate-500">
                        Updated: {new Date(will.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Expiry Info */}
                  {daysRemaining !== null && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${
                      isExpired ? 'bg-red-500/10 border border-red-500/20'
                        : showExpiryWarning ? 'bg-amber-500/10 border border-amber-500/20'
                        : 'bg-slate-800/50'
                    }`}>
                      {isExpired ? (
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      ) : (
                        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        {isExpired ? (
                          <p className="text-red-400 text-sm font-medium">Access expired</p>
                        ) : (
                          <p className={`text-sm font-medium ${showExpiryWarning ? 'text-amber-400' : 'text-slate-300'}`}>
                            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                          </p>
                        )}
                        <p className="text-xs text-slate-500">Expires: {expiryDateFormatted}</p>
                      </div>
                    </div>
                  )}

                  {/* Progress Bar */}
                  {!isExpired && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Progress</span>
                        <span className={statusConfig.textColor}>Step {currentStep} of {totalSteps}</span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${will.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                          style={{ width: `${will.status === 'completed' ? 100 : progressPercent}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    {isExpired ? (
                      <Button
                        size="sm"
                        className="flex-1 bg-slate-700 text-slate-300 hover:bg-slate-600"
                        onClick={(e) => { e.stopPropagation(); handleEditWill(will.id); }}
                      >
                        <Eye size={14} className="mr-1" /> View (Read-Only)
                      </Button>
                    ) : will.status === 'draft' ? (
                      <Button
                        size="sm"
                        className="flex-1 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30"
                        onClick={(e) => { e.stopPropagation(); handleEditWill(will.id); }}
                      >
                        <Edit size={14} className="mr-1" /> Continue Editing
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30"
                        onClick={(e) => { e.stopPropagation(); handleEditWill(will.id); }}
                      >
                        View Will
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={(e) => { e.stopPropagation(); handleDeleteWill(will.id); }}
                      data-testid={`delete-will-${will.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* How It Works */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white">How It Works</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Create a Draft', description: 'Start a new will and fill in the testator details on their behalf' },
              { step: '2', title: 'Complete All Steps', description: 'Go through all 9 steps including assets, beneficiaries, and special wishes' },
              { step: '3', title: 'Pay & Finalize', description: 'Complete payment at the final step to generate the legally valid will document' },
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-slate-800/30 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-amber-400 font-bold text-xl">{item.step}</span>
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Draft Limit Modal */}
      <DraftLimitModal
        isOpen={showDraftLimitModal}
        onClose={() => setShowDraftLimitModal(false)}
        onDelete={handleDeleteDraftFromLimit}
        drafts={wills.filter(w => !w.is_paid && w.status !== 'completed')}
        maxDrafts={maxUnpaidDrafts}
      />
    </div>
  );
};

export default PartnerWillManagement;
